/**
 * ensureModelViewer — resolve when the <model-viewer> custom element is
 * usable.
 *
 * The element is normally defined by /model-viewer.min.js, loaded as its own
 * module script from index.html (so the boot-splash 3D mascot renders without
 * waiting for the app bundle). Pages must NOT blindly
 * `import('@google/model-viewer')` after that: a second module instance calls
 * customElements.define('model-viewer') again and throws, rejecting the
 * import and silently breaking that page's 3D block. This guard checks the
 * registry first and only falls back to the npm package when the global
 * script didn't load (blocked CDN, ancient browser, tests).
 */
export function ensureModelViewer(): Promise<void> {
  // index.html probes WebGL once at boot (window.__webglOK). Without WebGL,
  // model-viewer's renderer singleton throws "reading 'xr'" on every element
  // upgrade — reject so 3D blocks fall back instead of crashing.
  if (typeof window !== 'undefined' && (window as any).__webglOK === false) {
    return Promise.reject(new Error('model-viewer unavailable (no WebGL)'));
  }
  if (typeof customElements !== 'undefined' && customElements.get('model-viewer')) {
    return Promise.resolve();
  }
  return import('@google/model-viewer').then(() => undefined).catch(() => {
    // Race: the global script may have defined it between our check and the
    // import's define call. If the element exists now, we're fine.
    if (typeof customElements !== 'undefined' && customElements.get('model-viewer')) return;
    throw new Error('model-viewer unavailable');
  });
}
